/**
 * Session Lock Configuration
 * 
 * Maps API endpoints to their lock requirements.
 * Specifies which endpoints require locks and how to extract lock parameters from requests.
 */

import store from '../store';

export type LockKeyType = 'DLO_SCHEDULE' | 'SINGLE_ID';

export interface LockConfig {
  /**
   * Endpoint pattern (regex or exact match)
   */
  endpoint: string | RegExp;
  
  /**
   * HTTP methods that require locks
   */
  methods: ('GET' | 'POST' | 'PUT' | 'DELETE')[];
  
  /**
   * Type of lock key to use
   */
  keyType: LockKeyType;
  
  /**
   * Module name for lock acquisition
   */
  module: string;
  
  /**
   * Submodule name for lock acquisition
   */
  submodule: string;
  
  /**
   * Function to extract lock parameters from request
   * Returns the parameters needed for lock acquisition
   */
  extractLockParams: (config: any) => {
    id?: string;
    locId?: string;
    period?: number;
    week?: number;
    year?: number;
  } | null;
}

/**
 * Lock configuration for all endpoints that require session locks
 */
export const LOCK_CONFIGS: LockConfig[] = [
  // DLO Schedule endpoints
  {
    endpoint: /\/dlo\/schedule(?:\/|$)/,
    methods: ['POST', 'PUT'],
    keyType: 'DLO_SCHEDULE',
    module: 'DLO',
    submodule: 'Schedule',
    extractLockParams: (config: any) => {
      // Extract from request body
      const data = config.data || {};
      
      // For DLO Schedule, we need: locId, period, week, year
      if (data.locId && data.period !== undefined && data.week && data.year) {
        return {
          locId: data.locId,
          period: data.period,
          week: data.week,
          year: data.year,
        };
      }

      // Try to extract from URL params
      const params = config.params || {};
      if (params.locId && params.period !== undefined && params.week && params.year) {
        return {
          locId: params.locId,
          period: params.period,
          week: params.week,
          year: params.year,
        };
      }

      return null;
    },
  },

  // DLO Schedule Settings endpoints
  {
    endpoint: /\/dlo\/schedule-settings(?:\/|$)/,
    methods: ['POST', 'PUT'],
    keyType: 'DLO_SCHEDULE',
    module: 'DLO',
    submodule: 'Schedule',
    extractLockParams: (config: any) => {
      const data = config.data || {};
      const params = config.params || {};
      
      // Check body first
      if (data.locId && data.period !== undefined && data.week && data.year) {
        return {
          locId: data.locId,
          period: data.period,
          week: data.week,
          year: data.year,
        };
      }

      // Then check params
      if (params.locId && params.period !== undefined && params.week && params.year) {
        return {
          locId: params.locId,
          period: params.period,
          week: params.week,
          year: params.year,
        };
      }

      return null;
    },
  },

  // DLO Clean Settings endpoint
  {
    endpoint: /\/dlo\/schedules\/cleanSettings(?:\/|$)/,
    methods: ['POST', 'PUT', 'DELETE'],
    keyType: 'DLO_SCHEDULE',
    module: 'DLO',
    submodule: 'Schedule',
    extractLockParams: (config: any) => {
      const data = config.data || {};
      const params = config.params || {};
      
      // Check body first
      if (data.locId && data.period !== undefined && data.week && data.year) {
        return {
          locId: data.locId,
          period: data.period,
          week: data.week,
          year: data.year,
        };
      }

      // Then check params
      if (params.locId && params.period !== undefined && params.week && params.year) {
        return {
          locId: params.locId,
          period: params.period,
          week: params.week,
          year: params.year,
        };
      }

      // As fallback, get from Redux state (for cleanSettings which is called after schedule is selected)
      try {
        const state = store.getState();
        const createSchedule = state.dlo?.createSchedule;
        const appData = state.appData;

        // Get lock parameters from createSchedule context
        if (appData?.selectedLocation?.id && createSchedule) {
          const locId = appData.selectedLocation.id;
          const period = createSchedule.period;
          const week = createSchedule.week;
          const year = createSchedule.year;

          if (locId && period !== undefined && week && year) {
            return {
              locId,
              period,
              week,
              year,
            };
          }
        }
      } catch (error) {
        // Silently fail - not critical if we can't get from Redux
      }

      return null;
    },
  },

  // DLO ScheduleSettings Forecast endpoint
  {
    endpoint: /\/dlo\/ScheduleSettings\/Forecast(?:\/|$)/,
    methods: ['PUT'],
    keyType: 'DLO_SCHEDULE',
    module: 'DLO',
    submodule: 'Schedule',
    extractLockParams: (config: any) => {
      const data = config.data || {};
      const params = config.params || {};
      
      // Check body first
      if (data.locId && data.period !== undefined && data.week && data.year) {
        return {
          locId: data.locId,
          period: data.period,
          week: data.week,
          year: data.year,
        };
      }

      // Then check params
      if (params.locId && params.period !== undefined && params.week && params.year) {
        return {
          locId: params.locId,
          period: params.period,
          week: params.week,
          year: params.year,
        };
      }

      // Redux fallback
      try {
        const state = store.getState();
        const createSchedule = state.dlo?.createSchedule;
        const appData = state.appData;

        if (appData?.selectedLocation?.id && createSchedule) {
          const locId = appData.selectedLocation.id;
          const period = createSchedule.period;
          const week = createSchedule.week;
          const year = createSchedule.year;

          if (locId && period !== undefined && week && year) {
            return { locId, period, week, year };
          }
        }
      } catch (error) {
        // Silently fail
      }

      return null;
    },
  },

  // DLO ScheduleSettings Forecast Daily endpoint
  {
    endpoint: /\/dlo\/ScheduleSettings\/Forecast\/Daily(?:\/|$)/,
    methods: ['PUT'],
    keyType: 'DLO_SCHEDULE',
    module: 'DLO',
    submodule: 'Schedule',
    extractLockParams: (config: any) => {
      const data = config.data || {};
      const params = config.params || {};
      
      if (data.locId && data.period !== undefined && data.week && data.year) {
        return { locId: data.locId, period: data.period, week: data.week, year: data.year };
      }

      if (params.locId && params.period !== undefined && params.week && params.year) {
        return { locId: params.locId, period: params.period, week: params.week, year: params.year };
      }

      try {
        const state = store.getState();
        const createSchedule = state.dlo?.createSchedule;
        const appData = state.appData;

        if (appData?.selectedLocation?.id && createSchedule) {
          const locId = appData.selectedLocation.id;
          const period = createSchedule.period;
          const week = createSchedule.week;
          const year = createSchedule.year;

          if (locId && period !== undefined && week && year) {
            return { locId, period, week, year };
          }
        }
      } catch (error) {
        // Silently fail
      }

      return null;
    },
  },

  // DLO ScheduleSettings Actual endpoint
  {
    endpoint: /\/dlo\/ScheduleSettings\/Actual(?:\/|$)/,
    methods: ['PUT'],
    keyType: 'DLO_SCHEDULE',
    module: 'DLO',
    submodule: 'Schedule',
    extractLockParams: (config: any) => {
      const data = config.data || {};
      const params = config.params || {};
      
      if (data.locId && data.period !== undefined && data.week && data.year) {
        return { locId: data.locId, period: data.period, week: data.week, year: data.year };
      }

      if (params.locId && params.period !== undefined && params.week && params.year) {
        return { locId: params.locId, period: params.period, week: params.week, year: params.year };
      }

      try {
        const state = store.getState();
        const createSchedule = state.dlo?.createSchedule;
        const appData = state.appData;

        if (appData?.selectedLocation?.id && createSchedule) {
          const locId = appData.selectedLocation.id;
          const period = createSchedule.period;
          const week = createSchedule.week;
          const year = createSchedule.year;

          if (locId && period !== undefined && week && year) {
            return { locId, period, week, year };
          }
        }
      } catch (error) {
        // Silently fail
      }

      return null;
    },
  },

  // DLO ScheduleSettings Actual Daily endpoint
  {
    endpoint: /\/dlo\/ScheduleSettings\/Actual\/Daily(?:\/|$)/,
    methods: ['PUT'],
    keyType: 'DLO_SCHEDULE',
    module: 'DLO',
    submodule: 'Schedule',
    extractLockParams: (config: any) => {
      const data = config.data || {};
      const params = config.params || {};
      
      if (data.locId && data.period !== undefined && data.week && data.year) {
        return { locId: data.locId, period: data.period, week: data.week, year: data.year };
      }

      if (params.locId && params.period !== undefined && params.week && params.year) {
        return { locId: params.locId, period: params.period, week: params.week, year: params.year };
      }

      try {
        const state = store.getState();
        const createSchedule = state.dlo?.createSchedule;
        const appData = state.appData;

        if (appData?.selectedLocation?.id && createSchedule) {
          const locId = appData.selectedLocation.id;
          const period = createSchedule.period;
          const week = createSchedule.week;
          const year = createSchedule.year;

          if (locId && period !== undefined && week && year) {
            return { locId, period, week, year };
          }
        }
      } catch (error) {
        // Silently fail
      }

      return null;
    },
  },

  // DLO Schedule status endpoint
  {
    endpoint: /\/dlo\/schedule\/status(?:\/|$)/,
    methods: ['PUT'],
    keyType: 'DLO_SCHEDULE',
    module: 'DLO',
    submodule: 'Schedule',
    extractLockParams: (config: any) => {
      const data = config.data || {};
      const params = config.params || {};
      
      if (data.locId && data.period !== undefined && data.week && data.year) {
        return { locId: data.locId, period: data.period, week: data.week, year: data.year };
      }

      if (params.locId && params.period !== undefined && params.week && params.year) {
        return { locId: params.locId, period: params.period, week: params.week, year: params.year };
      }

      try {
        const state = store.getState();
        const createSchedule = state.dlo?.createSchedule;
        const appData = state.appData;

        if (appData?.selectedLocation?.id && createSchedule) {
          const locId = appData.selectedLocation.id;
          const period = createSchedule.period;
          const week = createSchedule.week;
          const year = createSchedule.year;

          if (locId && period !== undefined && week && year) {
            return { locId, period, week, year };
          }
        }
      } catch (error) {
        // Silently fail
      }

      return null;
    },
  },

  // DLO Schedule future endpoint (DELETE)
  {
    endpoint: /\/dlo\/schedule\/future(?:\/|$)/,
    methods: ['DELETE'],
    keyType: 'DLO_SCHEDULE',
    module: 'DLO',
    submodule: 'Schedule',
    extractLockParams: (config: any) => {
      const data = config.data || {};
      const params = config.params || {};
      
      if (data.locId && data.period !== undefined && data.week && data.year) {
        return { locId: data.locId, period: data.period, week: data.week, year: data.year };
      }

      if (params.locId && params.period !== undefined && params.week && params.year) {
        return { locId: params.locId, period: params.period, week: params.week, year: params.year };
      }

      try {
        const state = store.getState();
        const createSchedule = state.dlo?.createSchedule;
        const appData = state.appData;

        if (appData?.selectedLocation?.id && createSchedule) {
          const locId = appData.selectedLocation.id;
          const period = createSchedule.period;
          const week = createSchedule.week;
          const year = createSchedule.year;

          if (locId && period !== undefined && week && year) {
            return { locId, period, week, year };
          }
        }
      } catch (error) {
        // Silently fail
      }

      return null;
    },
  },

  // Stand-up endpoints (edit, delete) - requires lock
  // Matches: /stand-up or /stand-up/{numeric-id}
  // Does NOT match: /stand-up/favourites or any other sub-path
  {
    endpoint: /\/stand-up(?:\/\d+)?$/,
    methods: ['PUT', 'DELETE'],
    keyType: 'SINGLE_ID',
    module: 'STANDUP',
    submodule: 'STANDUP',
    extractLockParams: (config: any) => {
      const data = config.data || {};
      const params = config.params || {};
      
      // Extract standup ID from request body or params
      const id = data.id || data.standup_id || data.standupId || data.ID ||
                 params.id || params.standup_id || params.standupId || params.ID;
      
      return id ? { id: id.toString() } : null;
    },
  },

  // Add more lock configurations here as needed
];

/**
 * Check if an endpoint requires a lock
 */
export const shouldAcquireLock = (endpoint: string, method: string): boolean => {
  // Strip query parameters
  const cleanEndpoint = endpoint.split('?')[0];
  
  return LOCK_CONFIGS.some(config => {
    const endpointMatch = typeof config.endpoint === 'string'
      ? config.endpoint === cleanEndpoint
      : config.endpoint.test(cleanEndpoint);
    
    return endpointMatch && config.methods.includes(method as any);
  });
};

/**
 * Get lock configuration for an endpoint
 */
export const getLockConfig = (endpoint: string, method: string): LockConfig | undefined => {
  // Strip query parameters
  const cleanEndpoint = endpoint.split('?')[0];
  
  return LOCK_CONFIGS.find(config => {
    const endpointMatch = typeof config.endpoint === 'string'
      ? config.endpoint === cleanEndpoint
      : config.endpoint.test(cleanEndpoint);
    
    return endpointMatch && config.methods.includes(method as any);
  });
};
